/* eslint-disable @typescript-eslint/no-misused-promises */
import Image from 'next/image';
import { FC, useState, useRef, useEffect } from 'react';
import { useFormContext } from '../../../../../contexts/FormContext';
import { useOutputAreaContext } from '../../../../../contexts/OuputAreaContext';
import { useResponseHistoryContext } from '../../../../../contexts/ResponseHistoryContext';
import { FileTree } from '../../../savedContentPage/fileTree';
import styles from './CardButtons.module.css';
import Markdown from 'markdown-to-jsx';
import axios from 'axios';
import { marked } from 'marked';
import { useRouter } from 'next/router';

type CardButtonsProps = {
  responseIndex: number;
  id: string;
};

const CustomH1: React.FC<{ children: string }> = ({ children, ...props }) => (
  <b {...props} style={{ lineHeight: '1.5' }}>
    #{children}
  </b>
);

const CustomMarkdown: React.FC<{ children: string }> = ({ children }) => {
  return (
    <Markdown
      options={{
        overrides: {
          h1: {
            component: CustomH1,
          },
        },
      }}
    >
      {children}
    </Markdown>
  );
};



export const CardButtons: FC<CardButtonsProps> = ({ responseIndex, id }) => {
  const { generatedOutput, setGeneratedOutput, editedOutput, setEditedOutput } =
    useFormContext();
  const {
    copyResponse,
    saveFileClicked,
    reasoningClicked,
    reasoningID,
    showReasoning,
    setShowReasoning,
    setReasoningID,
    sendToMedia,
    showMediaPage
  } = useOutputAreaContext();
  const { likeResponse, dislikeResponse, favoriteResponse } =
    useResponseHistoryContext();
  const [showFileTree, setShowFileTree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuRef2 = useRef<HTMLDivElement>(null);
  
const router = useRouter();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowReasoning(false);
      }
      if (menuRef2.current && !menuRef2.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [setShowReasoning, setShowExportMenu]);

  let markText = '';

  const handleExportPDF = async (index: number) => {
    setLoading(true);
    try {
      const text = editedOutput[index].text
        .replace(/^"|"$/g, '')
        .replace(/\\n/g, '\n');
      markText = await marked(text);

      const response = await axios.post(
        '/api/exporting/puppeteerForPDF',
        { html: markText },
        {
          responseType: 'blob',
        }
      );

      if (response.status === 200) {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'generated-pdf.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error('Error generating PDF: ', response.statusText);
      }
    } catch (error) {
      console.error('Error exporting PDF: ', error);
    }
    setLoading(false);
  };

  const sendHtmlForDocx = async (index: number) => {
    try {
      setLoading(true);
      const text = editedOutput[index].text
        .replace(/^"|"$/g, '')
        .replace(/\\n/g, '\n');
      markText = await marked(text);

      const response = await axios.post(
        '/api/exporting/exportToDocX',
        { html: markText },
        {
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'generated-docx.docx');
      document.body.appendChild(link);
      link.click();
      setLoading(false);
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error generating DOCX:', error);
    }
  };

  const handleExportTXT = async (index: number) => {
    setLoading(true);
    const text = editedOutput[index].text
      .replace(/^"|"$/g, '')
      .replace(/\\n/g, '\n');
    markText = await marked(text);
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = markText;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'generated-text.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowExportMenu(false);
    setLoading(false);
  };

  const handleExportMarkdown = async (index: number) => {
    setLoading(true);
    const text = editedOutput[index].text
      .replace(/^"|"$/g, '')
      .replace(/\\n/g, '\n');
    markText = await marked(text);
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = markText;
    const blob = new Blob([markText], { type: 'text/markdown;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'generated-markdown.md';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowExportMenu(false);
    setLoading(false);
  };

    useEffect(() => {
      if (showMediaPage) {
          router.push('/media'); // Redirects to media page
      }
  }, [showMediaPage]);


  return (
    <div className={styles['container']}>
      <div className={styles['button1']}>
        <Image
          src="/icons/Copy_New.svg"
          alt="Copy"
          layout="fixed"
          objectFit="contain"
          height={16}
          width={16}
          onClick={() => {
            copyResponse(responseIndex);
          }}
        />
        <div className={styles['tooltip1']}>
          <p>Copy Response</p>
        </div>
      </div>
      <div className={styles['button2']}>
        <Image
          src="/icons/Heart_New.svg"
          alt="Like"
          layout="fixed"
          objectFit="contain"
          height={16}
          width={16}
          onClick={async () => {
            setLoading(true);
            await favoriteResponse(responseIndex);
            setLoading(false);
          }}
        />
        <div className={styles['tooltip2']}>
          <p>Favorite Response</p>
        </div>
      </div>
      <div className={styles['button3']}>
        <Image
          src="/icons/Folder_New.svg"
          alt="Like"
          layout="fixed"
          objectFit="contain"
          height={16}
          width={16}
          onClick={() => {
            saveFileClicked(id);
          }}
        />
        <div className={styles['tooltip3']}>
          <p>Save Response</p>
        </div>
      </div>
      <div className={styles['button4']}>
        <Image
          src={loading ? '/demoGifs/animation.gif' : '/icons/Export.svg'}
          alt="Like"
          layout="fixed"
          objectFit="contain"
          height={16}
          width={16}
          onClick={() => {
            setShowExportMenu(!showExportMenu);
          }}
        />
        <div className={styles['tooltip4']}>
          <p>Export</p>
        </div>
      </div>
      {showExportMenu && (
        <div className={styles['dropdown-content2']} ref={menuRef2}>
          <div className={styles['dropdown-content-item2']} onClick={() => {}}>
            <p>Pick an export format:</p>
          </div>
          <div
            className={styles['dropdown-content-item']}
            onClick={async () => {
              await sendHtmlForDocx(responseIndex);
            }}
          >
            <Image
              src={loading ? '/demoGifs/animation.gif' : '/icons/Word Icon.svg'}
              alt="Close"
              layout="fixed"
              objectFit="contain"
              height={16}
              width={16}
            />
            <p>Word Document</p>
          </div>
          <div
            className={styles['dropdown-content-item']}
            onClick={async () => {
              await handleExportMarkdown(responseIndex);
            }}
          >
            <Image
              src={loading ? '/demoGifs/animation.gif' : '/icons/Scroll.svg'}
              alt="Close"
              layout="fixed"
              objectFit="contain"
              height={16}
              width={16}
            />
            <p>Markdown</p>
          </div>
          <div
            className={styles['dropdown-content-item']}
            onClick={async () => {
              await handleExportTXT(responseIndex);
            }}
          >
            <Image
              src={loading ? '/demoGifs/animation.gif' : '/icons/Scroll.svg'}
              alt="Close"
              layout="fixed"
              objectFit="contain"
              height={16}
              width={16}
            />
            <p>Plain Text</p>
          </div>
        </div>
      )}
      <div className={styles['button-media']}>
        <Image
          src="/icons/Media.svg"
          alt="Media"
          layout="fixed"
          objectFit="contain"
          height={16}
          width={16}
          onClick={() => {
            sendToMedia(editedOutput[responseIndex].text); // ✅ Sends full text
          }}
        />
        <div className={styles['tooltip-media']}>
          <p>Send to Media</p>
        </div>
      </div>

      <div className={styles['button4']}>
        <Image
          id="icon"
          src={
            reasoningID === id && showReasoning
              ? '/icons/AI Sparkle Icon_orange.svg'
              : '/icons/AI Sparkle Icon_New.svg'
          }
          alt="Like"
          layout="fixed"
          objectFit="contain"
          height={16}
          width={16}
          onClick={() => {
            if (reasoningID === id && showReasoning) {
              setShowReasoning(false);
            } else if (editedOutput[responseIndex].reasoning) {
              setShowReasoning(true);
              setReasoningID(id);
            } else {
              setLoading(true);
              reasoningClicked(id);
              setLoading(false);
            }
          }}
        />
        <div className={styles['tooltip4']}>
          <p>AI-Reasoning</p>
        </div>
      </div>
      {showReasoning && reasoningID === id && (
        <div className={styles['reasoning']} id="popup" ref={menuRef}>
          <div className={styles['header']}>
            <Image
              src={'/icons/AI Sparkle Icon_orange.svg'}
              alt="Like"
              layout="fixed"
              objectFit="contain"
              height={20}
              width={20}
            />
            <h3>AI-Reasoning</h3>
          </div>
          <div onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
            <CustomMarkdown>
              {editedOutput[responseIndex].reasoning ?? ''}
            </CustomMarkdown>
          </div>
        </div>
      )}
    </div>
  );
};